import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Badge, Button, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { Bifa, Grup } from './comune'

type Tva = {
  id: number; code: string; label: string; percent: number; is_margin: boolean
  for_regions: string; is_active: boolean; is_system: boolean; sort_order: number
}
type Istoric = {
  id: number; vat_code: string; action: string; field_name: string | null
  old_value: string | null; new_value: string | null; user_name: string | null; created_at: string | null
}

const REGIUNI = ['ALL', 'RO', 'UE', 'NON_UE']
const gol = { code: '', label: '', percent: '0', is_margin: false, for_regions: ['ALL'] }

const clipa = (s: string | null) =>
  s ? new Date(s).toLocaleString('ro-RO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

const citesteRegiuni = (raw: string): string[] => {
  try {
    const v = JSON.parse(raw)
    return Array.isArray(v) ? (v as string[]) : ['ALL']
  } catch {
    return ['ALL']
  }
}

export function SetariTva() {
  const qc = useQueryClient()
  const [editez, setEditez] = useState<number | 'nou' | null>(null)
  const [f, setF] = useState<typeof gol>(gol)
  const [eroare, setEroare] = useState<string | null>(null)

  const lista = useQuery({
    queryKey: ['tva-lista'],
    queryFn: () => api.get<{ vat_types: Tva[] }>('/vat-settings/list'),
  })
  const istoric = useQuery({
    queryKey: ['tva-istoric'],
    queryFn: () => api.get<{ history: Istoric[] }>('/vat-settings/history/list', { limit: 20 }),
  })

  const reia = () => {
    qc.invalidateQueries({ queryKey: ['tva-lista'] })
    qc.invalidateQueries({ queryKey: ['tva-istoric'] })
  }
  const prinde = (e: unknown) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva')

  const salveaza = useMutation({
    mutationFn: () => {
      const corp = {
        code: f.code, label: f.label, percent: Number(f.percent) || 0,
        is_margin: f.is_margin, for_regions: f.for_regions,
      }
      return editez === 'nou'
        ? api.post('/vat-settings/', corp)
        : api.put(`/vat-settings/${editez}`, corp)
    },
    onSuccess: () => { setEditez(null); setF(gol); reia() },
    onError: prinde,
  })
  const comuta = useMutation({
    mutationFn: (id: number) => api.post(`/vat-settings/${id}/toggle`),
    onSuccess: reia, onError: prinde,
  })
  const sterge = useMutation({
    mutationFn: (id: number) => api.del(`/vat-settings/${id}`),
    onSuccess: reia, onError: prinde,
  })
  const muta = useMutation({
    mutationFn: (ordine: number[]) => api.post('/vat-settings/reorder', { vat_type_ids: ordine }),
    onSuccess: reia, onError: prinde,
  })
  const populeaza = useMutation({
    mutationFn: () => api.post('/vat-settings/seed'),
    onSuccess: reia, onError: prinde,
  })

  if (lista.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={8} /></Card></div>
  const tipuri = lista.data?.vat_types ?? []

  const deschide = (t?: Tva) => {
    setEroare(null)
    if (t) {
      setEditez(t.id)
      setF({ code: t.code, label: t.label, percent: String(t.percent), is_margin: t.is_margin, for_regions: citesteRegiuni(t.for_regions) })
    } else {
      setEditez('nou')
      setF(gol)
    }
  }

  /** Reordonarea muta un tip cu o pozitie, in lista trimisa intreaga — serverul rescrie
   *  sort_order dupa ordinea primita. */
  const mutaCu = (idx: number, directie: -1 | 1) => {
    const noua = [...tipuri]
    const tinta = idx + directie
    if (tinta < 0 || tinta >= noua.length) return
    const a = noua[idx]
    const b = noua[tinta]
    if (!a || !b) return
    noua[idx] = b
    noua[tinta] = a
    muta.mutate(noua.map((t) => t.id))
  }

  return (
    <div className="px-8 py-6 space-y-5">
      <Card className="p-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13.5px] text-muted">
          Cotele se aleg pe linia de factura. „Regim marja" = se impoziteaza doar comisionul
          agentiei, nu intreg pretul pachetului.
        </p>
        <div className="flex gap-2">
          {tipuri.length === 0 && (
            <Button onClick={() => populeaza.mutate()} disabled={populeaza.isPending}>
              Adauga cotele standard
            </Button>
          )}
          <Button variant="primary" onClick={() => deschide()}>Tip nou</Button>
        </div>
      </Card>

      {eroare && <Card className="p-3"><span className="text-[13px] text-bad">{eroare}</span></Card>}

      {editez !== null && (
        <Grup titlu={editez === 'nou' ? 'Tip nou de TVA' : 'Modifica tipul de TVA'}>
          <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
            <Camp eticheta="Cod" id="tva-cod" valoare={f.code} seteaza={(v) => setF((x) => ({ ...x, code: v }))}
              indiciu="Ex.: A.311, TI, 21%" obligatoriu />
            <Camp eticheta="Denumire" id="tva-den" valoare={f.label} seteaza={(v) => setF((x) => ({ ...x, label: v }))}
              obligatoriu />
            <Camp eticheta="Procent" id="tva-proc" tip="number" valoare={f.percent}
              seteaza={(v) => setF((x) => ({ ...x, percent: v }))} indiciu="0 pentru regimul de marja" />
            <div className="sm:col-span-2 space-y-2">
              <Bifa eticheta="Regim de marja" valoare={f.is_margin}
                seteaza={(v) => setF((x) => ({ ...x, is_margin: v }))} />
              <div>
                <p className="text-[12.5px] text-muted mb-1">Se aplica pentru</p>
                <div className="flex flex-wrap gap-3">
                  {REGIUNI.map((r) => (
                    <Bifa key={r} eticheta={r} valoare={f.for_regions.includes(r)}
                      seteaza={(v) => setF((x) => ({
                        ...x,
                        for_regions: v ? [...x.for_regions, r] : x.for_regions.filter((z) => z !== r),
                      }))} />
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <Button variant="primary" onClick={() => { setEroare(null); salveaza.mutate() }}
              disabled={salveaza.isPending || !f.code || !f.label}>
              {salveaza.isPending ? 'Se salveaza…' : 'Salveaza'}
            </Button>
            <Button variant="quiet" onClick={() => { setEditez(null); setEroare(null) }}>Renunta</Button>
          </div>
        </Grup>
      )}

      <Card className="overflow-hidden">
        {tipuri.length === 0 ? (
          <EmptyState title="Niciun tip de TVA" hint={'Apasa „Adauga cotele standard" ca sa pornesti de la cele uzuale.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="text-[12.5px] text-muted border-b border-line">
                <tr>
                  <th className="text-left font-medium px-4 py-2.5">Cod</th>
                  <th className="text-left font-medium px-4 py-2.5">Denumire</th>
                  <th className="text-right font-medium px-4 py-2.5">Procent</th>
                  <th className="text-left font-medium px-4 py-2.5">Se aplica</th>
                  <th className="text-left font-medium px-4 py-2.5">Stare</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {tipuri.map((t, i) => (
                  <tr key={t.id} className="border-b border-line last:border-0">
                    <td className="px-4 py-2.5 font-medium">{t.code}</td>
                    <td className="px-4 py-2.5">
                      {t.label}
                      {t.is_margin && <span className="ml-2 text-[12px] text-muted">regim de marja</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right tnum">{t.percent}%</td>
                    <td className="px-4 py-2.5 text-[13px] text-muted">{citesteRegiuni(t.for_regions).join(', ')}</td>
                    <td className="px-4 py-2.5">
                      <Badge tone={t.is_active ? 'good' : 'neutral'}>{t.is_active ? 'activ' : 'oprit'}</Badge>
                      {t.is_system && <span className="ml-2 text-[12px] text-muted">sistem</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button variant="quiet" onClick={() => mutaCu(i, -1)} disabled={i === 0 || muta.isPending}>↑</Button>
                        <Button variant="quiet" onClick={() => mutaCu(i, 1)} disabled={i === tipuri.length - 1 || muta.isPending}>↓</Button>
                        <Button variant="quiet" onClick={() => deschide(t)}>Modifica</Button>
                        <Button variant="quiet" onClick={() => comuta.mutate(t.id)} disabled={comuta.isPending}>
                          {t.is_active ? 'Opreste' : 'Porneste'}
                        </Button>
                        {!t.is_system && (
                          <Button variant="quiet" onClick={() => {
                            if (confirm(`Stergi definitiv tipul ${t.code}?`)) sterge.mutate(t.id)
                          }}>Sterge</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Grup titlu="Ultimele modificari">
        {istoric.data?.history?.length ? (
          <ul className="space-y-1.5 text-[13.5px]">
            {istoric.data.history.map((h) => (
              <li key={h.id} className="flex flex-wrap gap-x-2">
                <span className="text-muted tnum">{clipa(h.created_at)}</span>
                <span className="font-medium">{h.vat_code}</span>
                <span>{h.action}</span>
                {h.field_name && <span className="text-muted">{h.field_name}: {h.old_value ?? '—'} → {h.new_value ?? '—'}</span>}
                {h.user_name && <span className="text-muted">({h.user_name})</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[13.5px] text-muted">Nicio modificare inregistrata.</p>
        )}
      </Grup>
    </div>
  )
}
